import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { create } from 'zustand';
import { setUserLocale, setUserTimezone } from './utils/datetime';
import { buildInvitationLink } from './utils/invitationLink';

// Import pages
import Layout from './components/Layout';
import LoginPage from './pages/Login';
import VerifyInvoicePage from './pages/VerifyInvoice';
import DashboardPage from './pages/Dashboard';
import MoFamilyPage from './pages/MoFamily';
import MoFamilyInvitationPage from './pages/MoFamilyInvitation';
import MoFilePage from './pages/MoFile';
import BasicPage from './pages/Basic'; // BASIC 모드(격리) — 제거 시 이 줄과 /basic 라우트만 삭제
import MoMailPage from './pages/MoMail';
import InboxV2Page from './pages/messages/Inbox';
import ConversationViewPage from './pages/messages/ConversationView';
import ProfilePage from './pages/Profile';
import CampaignPage from './pages/Campaign';
import CampaignForm from './pages/CampaignForm';
import CampaignDetail from './pages/CampaignDetail';
import MoClientPage from './pages/MoClient';
// import MoControllerPage from './pages/MoController'; // 미사용 — 리모컨은 MoClient.tsx 내장
import MarketLanding from './pages/market/MarketLanding';
import ListingDetail from './pages/market/ListingDetail';
import ProposalInbox from './pages/market/ProposalInbox';
import ProposalThread from './pages/market/ProposalThread';
import Transactions from './pages/market/Transactions';
import MarketAdmin from './pages/admin/MarketAdmin';
import UsersAdmin from './pages/admin/UsersAdmin';
import DeviceSetupGuide from './pages/admin/DeviceSetupGuide';
import ProposalPages from './pages/admin/ProposalPages';
import MoboardCollector from './pages/admin/MoboardCollector';
import DeviceGuide from './pages/DeviceGuide';
import PluginGuide from './pages/PluginGuide';
import AdminAccessPolicy from './pages/AdminAccessPolicy';
import PublicUploadPage from './pages/PublicUpload';
import EmbedCalendar from './pages/EmbedCalendar';

// Import components
import NotificationToast, { useNotificationStore } from './components/NotificationToast';

// Import stores
import { moFamilyStore } from './stores/moFamilyStore';
import { useFileTranscodeStore } from './stores/fileTranscodeStore';

// Types
interface User {
 moPeerHash: string;
 email: string;
 name: string;
 profilePicture?: string;
 role?: string;
 isAdmin?: boolean;
 phone?: string;
 businessRegistrationNumber?: string;
 locale?: string | null;
 timezone?: string | null;
 lastLoginAt?: string | null;
 previousLoginAt?: string | null;
 lastLogoutAt?: string | null;
 adminAccessBlocked?: boolean;
 /** 스텝(마스커레이드) 로그인에게 내 사적 채팅(DM)도 공유할지. 기본 true. (114) */
 shareDirectWithStaff?: boolean;
}

interface ChatTarget {
 type: 'peer' | 'family';
 id: string;
 name: string;
 isOnline?: boolean;
}

interface ChatMessage {
 id: string;
 content: string;
 senderHash: string;
 senderName: string;
 recipientHash: string;
 recipientFamilyHash?: string;
 sentAt: string;
 type: 'peer' | 'family';
}

interface SudoInfo {
 asHash: string;
 asName: string | null;
 adminHash: string | null;
 startedAt: string | null;
 mode?: 'view' | 'operate';
}

interface ViewedByAdminInfo {
 adminName: string;
 startedAt: string;
}

interface AppState {
 user: User | null;
 isAuthenticated: boolean;
 isLoading: boolean;
 sudo: SudoInfo | null;
 viewedByAdmin: ViewedByAdminInfo | null;
 viewedByAdminDismissed: boolean;
 /** 마스커레이드(스텝→사장) 로그인 시 표시할 계정명. null=아님. (114) */
 masquerade: { accountName: string | null } | null;
 hasNewMail: boolean;
 unreadInboxCount: number;
 ws: WebSocket | null;
 processedMessageIds: Set<string>;

 // Chat state
 chatMessages: Map<string, ChatMessage[]>;
 activeChatTarget: ChatTarget | null;
 chatTargets: ChatTarget[];
 unreadChatCounts: Map<string, number>;
 isChatOpen: boolean;
 latestMessageSender: { id: string; name: string } | null;
 // 비로그인 업로드 도착 WS 알림 카운터 — 구독 컴포넌트(대시보드)가 이 값 변화를 refetch 트리거로 쓴다.
 pendingUploadTick: number;

 setUser: (user: User | null) => void;
 setAuthenticated: (auth: boolean) => void;
 setLoading: (loading: boolean) => void;
 setSudo: (sudo: SudoInfo | null) => void;
 setViewedByAdmin: (info: ViewedByAdminInfo | null) => void;
 dismissViewedByAdmin: () => void;
 setHasNewMail: (hasNew: boolean) => void;
 setUnreadInboxCount: (n: number) => void;
 refreshUnreadInbox: () => Promise<void>;
 setWebSocket: (ws: WebSocket | null) => void;

 // Chat methods
 setActiveChatTarget: (target: ChatTarget | null) => void;
 addChatMessage: (targetId: string, message: ChatMessage) => void;
 setChatMessages: (targetId: string, messages: ChatMessage[]) => void;
 setChatTargets: (targets: ChatTarget[]) => void;
 markChatAsRead: (targetId: string) => void;
 setChatOpen: (isOpen: boolean) => void;
 setUnreadCount: (targetId: string, count: number) => void;

 checkAuth: () => Promise<boolean>;
 logout: () => Promise<void>;
 connectWebSocket: () => void;
 handleWebSocketMessage: (message: any) => void;
}

// Global App Store
export const useAppStore = create<AppState>((set, get) => ({
 user: null,
 isAuthenticated: false,
 isLoading: true,
 sudo: null,
 viewedByAdmin: null,
 viewedByAdminDismissed: false,
 masquerade: null,
 hasNewMail: false,
 unreadInboxCount: 0,
 ws: null,
 processedMessageIds: new Set(),
 
 // Chat state
 chatMessages: new Map(),
 activeChatTarget: null,
 chatTargets: [],
 unreadChatCounts: new Map(),
 isChatOpen: false,
 latestMessageSender: null,
 pendingUploadTick: 0,

 setUser: (user) => set({ user }),
 setAuthenticated: (auth) => set({ isAuthenticated: auth }),
 setLoading: (loading) => set({ isLoading: loading }),
 setSudo: (sudo) => set({ sudo }),
 setViewedByAdmin: (info) => set({ viewedByAdmin: info, viewedByAdminDismissed: false }),
 dismissViewedByAdmin: () => set({ viewedByAdminDismissed: true }),
 setHasNewMail: (hasNew) => set({ hasNewMail: hasNew }),
 setUnreadInboxCount: (n) => set({ unreadInboxCount: n }),
 refreshUnreadInbox: async () => {
  // 로그인 상태에서만 조회 — 미인증 시 호출하면 401/불필요 요청이 콘솔에 남는다.
  if (!get().isAuthenticated) return;
  try {
   const r = await fetch('/messages/unread-count', { credentials: 'include' });
   const j = await r.json();
   if (j.success && typeof j.data?.count === 'number') {
    set({ unreadInboxCount: j.data.count });
   }
  } catch {
   // best-effort — 다음 갱신 기회에 정확화
  }
 },
 setWebSocket: (ws) => set({ ws }),
 
 // Chat methods
 setActiveChatTarget: (target) => {
 set({ activeChatTarget: target });
 if (target) {
 set((state) => {
 const newUnreadCounts = new Map(state.unreadChatCounts);
 newUnreadCounts.delete(target.id);
 return { unreadChatCounts: newUnreadCounts };
 });
 }
 },
 
 addChatMessage: (targetId, message) => set((state) => {
 const messages = new Map(state.chatMessages);
 const targetMessages = messages.get(targetId) || [];
 messages.set(targetId, [...targetMessages, message]);
 
 // 읽지 않은 메시지 카운트 증가 (활성 대상이 아니거나 채팅창이 닫혀있는 경우)
 const newUnreadCounts = new Map(state.unreadChatCounts);
 if (!state.isChatOpen || state.activeChatTarget?.id !== targetId) {
 const currentCount = newUnreadCounts.get(targetId) || 0;
 newUnreadCounts.set(targetId, currentCount + 1);
 
 // 최신 메시지 발신자 정보 저장
 if (message.senderHash !== state.user?.moPeerHash) {
 set({ latestMessageSender: { id: message.senderHash, name: message.senderName } });
 }
 }
 
 return { chatMessages: messages, unreadChatCounts: newUnreadCounts };
 }),
 
 setChatMessages: (targetId, messages) => set((state) => {
 const newMessages = new Map(state.chatMessages);
 newMessages.set(targetId, messages);
 return { chatMessages: newMessages };
 }),
 
 setChatTargets: (targets) => set({ chatTargets: targets }),
 
 markChatAsRead: (targetId) => set((state) => {
 const newUnreadCounts = new Map(state.unreadChatCounts);
 newUnreadCounts.delete(targetId);
 return { unreadChatCounts: newUnreadCounts };
 }),
 
 setChatOpen: (isOpen) => set({ isChatOpen: isOpen }),
 
 setUnreadCount: (targetId, count) => set((state) => {
 const newUnreadCounts = new Map(state.unreadChatCounts);
 if (count > 0) {
 newUnreadCounts.set(targetId, count);
 } else {
 newUnreadCounts.delete(targetId);
 }
 return { unreadChatCounts: newUnreadCounts };
 }),
 
 checkAuth: async () => {
 try {
 const response = await fetch('/auth/status', {
 credentials: 'include'
 });
 
 if (response.ok) {
 const data = await response.json();
 if (data.authenticated) {
 set({
 isAuthenticated: true,
 user: data.user,
 sudo: data.sudo ?? null,
 viewedByAdmin: data.viewedByAdmin ?? null,
 masquerade: data.masquerade ?? null,
 isLoading: false
 });
 // 사용자 표시 환경 — 시각 헬퍼에 즉시 반영
 setUserLocale(data.user?.locale ?? null);
 setUserTimezone(data.user?.timezone ?? null);
 // 인증된 경우에만 WebSocket 연결
 get().connectWebSocket();
 // 미열람 메시지 수 초기 fetch — best-effort
 get().refreshUnreadInbox();
 return true;
 }
 }
 
 // 인증되지 않은 경우 WebSocket 연결 해제
 const ws = get().ws;
 if (ws) {
 ws.close();
 set({ ws: null });
 }
 
 set({ isAuthenticated: false, user: null, sudo: null, viewedByAdmin: null, masquerade: null, isLoading: false });
 return false;
 } catch (error) {
 set({ isAuthenticated: false, user: null, sudo: null, viewedByAdmin: null, masquerade: null, isLoading: false });
 return false;
 }
 },
 
 logout: async () => {
 try {
 await fetch('/auth/logout', {
 method: 'POST',
 credentials: 'include'
 });
 
 const ws = get().ws;
 if (ws) {
 ws.close();
 }
 
 set({
 user: null,
 isAuthenticated: false,
 sudo: null,
 viewedByAdmin: null,
 ws: null
 });

 sessionStorage.clear();
 } catch (error) {
 }
 },
 
 connectWebSocket: () => {
 // 인증되지 않은 경우 WebSocket 연결하지 않음
 const state = get();
 if (!state.isAuthenticated || !state.user) {
 return;
 }
 
 const currentWs = state.ws;
 if (currentWs && currentWs.readyState === WebSocket.OPEN) {
 return;
 }
 
 const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
 const wsHost = window.location.host;
 // Note: WebSocket in browsers automatically includes cookies when connecting to same origin
 const ws = new WebSocket(`${wsProtocol}//${wsHost}/ws`);
 
 ws.onopen = () => {
 set({ ws });
 // 재연결 갭(서버 재시작 등) 동안 놓친 transcode_complete 를 복구 — 진행 중 항목 실제 상태 재조회.
 useFileTranscodeStore.getState().reconcileStale();
 };
 
 ws.onmessage = (event) => {
 try {
 const message = JSON.parse(event.data);
 get().handleWebSocketMessage(message);
 } catch (error) {
 }
 };
 
 ws.onclose = () => {
 set({ ws: null });
 // 끊긴 동안은 "시간이 조금 더 걸리고 있어요" 지연 판정을 멈춘다(배지 유틸은 sawWs 를 본다).
 useFileTranscodeStore.getState().markWsClosed();
 
 // 인증된 상태에서만 재연결 시도
 setTimeout(() => {
 const state = get();
 if (state.isAuthenticated && state.user) {
 get().connectWebSocket();
 }
 }, 5000);
 };
 },
 
 handleWebSocketMessage: (message) => {
 // Handle duplicate messages
 const { processedMessageIds } = get();
 if (message.id && processedMessageIds.has(message.id)) {
 return;
 }
 
 if (message.id) {
 processedMessageIds.add(message.id);
 // Keep only last 1000 message IDs
 if (processedMessageIds.size > 1000) {
 const firstId = processedMessageIds.values().next().value;
 processedMessageIds.delete(firstId);
 }
 }
 
 // Handle different message types
 switch (message.type) {
 case 'mail_received':
 case 'new_mail':
 set({ hasNewMail: true });
 get().refreshUnreadInbox();
 // Show notification for new mail
 const data = message.data || message.payload;
 if (data) {
 useNotificationStore.getState().addNotification({
 type: 'mail',
 title: '새 메일이 도착했습니다',
 message: `${data.senderName || data.senderEmail}: ${data.subject}`,
 link: '/momail'
 });
 }
 break;
 
 case 'invitation_sent':
 set({ hasNewMail: true });
 get().refreshUnreadInbox();
 if (message.payload) {
 useNotificationStore.getState().addNotification({
 type: 'invitation',
 title: '초대장이 도착했습니다',
 message: `${message.payload.inviterName}님이 ${message.payload.familyName}에 초대했습니다`,
 link: '/momail'
 });
 }
 break;
 
 case 'chat_message':
 if (message.payload) {
 const chatMessage = message.payload;
 const { user } = get();
 
 // 메시지 타겟 결정
 let targetId: string;
 let targetName: string;
 if (chatMessage.senderHash === user?.moPeerHash) {
 targetId = chatMessage.recipientHash;
 targetName = ''; // 내가 보낸 메시지는 알림 안함
 } else {
 targetId = chatMessage.senderHash;
 targetName = chatMessage.senderName || '알 수 없는 사용자';
 }
 
 // 메시지 추가
 get().addChatMessage(targetId, chatMessage);
 
 // 내가 받은 메시지일 때만 알림 표시
 if (chatMessage.senderHash !== user?.moPeerHash) {
 useNotificationStore.getState().addNotification({
 type: 'chat',
 title: '새 채팅 메시지',
 message: `${targetName}: ${chatMessage.content}`,
 onClick: async () => {
 // 대화 대상이 목록에 없으면 추가
 const existingTarget = get().chatTargets.find(t => t.id === targetId);
 const target = existingTarget || {
 type: 'peer' as const,
 id: targetId,
 name: targetName || chatMessage.senderName || '알 수 없는 사용자',
 isOnline: true
 };
 
 // 목록에 없으면 추가
 if (!existingTarget) {
 get().setChatTargets([target, ...get().chatTargets]);
 } else if (existingTarget.name === '알 수 없는 사용자' && targetName) {
 // 기존 대화 상대의 이름이 "알 수 없는 사용자"인 경우 업데이트
 const updatedTargets = get().chatTargets.map(t => 
 t.id === targetId ? { ...t, name: targetName } : t
 );
 get().setChatTargets(updatedTargets);
 }
 
 // 채팅창 열고 대화 선택
 get().setActiveChatTarget(target);
 get().setChatOpen(true);
 
 // 메시지 기록 로드
 try {
 const response = await fetch(`/chat/history?targetHash=${targetId}&limit=50`, {
 credentials: 'include'
 });
 
 if (response.ok) {
 const data = await response.json();
 get().setChatMessages(targetId, data.data);
 get().markChatAsRead(targetId);
 }
 } catch (error) {
 console.error('Failed to load chat history:', error);
 }
 }
 });
 }
 }
 break;
 
 case 'family_invitation_sent':
 set({ hasNewMail: true });
 get().refreshUnreadInbox();
 if (message.payload) {
 useNotificationStore.getState().addNotification({
 type: 'family_invitation',
 title: '모패밀리 협업 제안',
 message: `${message.payload.fromFamilyName}에서 협업을 제안했습니다`,
 link: buildInvitationLink({ code: message.payload.invitationCode })
 });
 }
 break;

 // 역제안 도착 — 원래 초대자에게 실시간 팝업. (없으면 inbox 갱신만 돼 팝업이 안 떴음)
 case 'family_invitation_countered':
 set({ hasNewMail: true });
 get().refreshUnreadInbox();
 if (message.payload) {
 useNotificationStore.getState().addNotification({
 type: 'family_invitation',
 title: '협업 역제안 도착',
 message: `${message.payload.toFamilyName ?? '상대 모패밀리'}에서 역제안을 보냈습니다`,
 // 역제안은 메시지 메뉴와 동일한 대화 화면(/inbox/:cid)으로 — 거기서만 재역제안 가능.
 // (standalone 초대 검토 페이지는 수락/거절만 있어 사용 안 함)
 link: message.payload.conversationId ? `/inbox/${message.payload.conversationId}` : '/inbox'
 });
 }
 break;

 // 통합 메시지 시스템 알림 — inbox unread 갱신 트리거
 case 'family_invitation_update':
 case 'family_invitation_notification':
 case 'market_proposal':
 case 'system_notification':
 get().refreshUnreadInbox();
 break;

 case 'pending_upload':
 // 비로그인 업로드 도착 → 대시보드 대기목록 refetch 트리거(카운터 증가).
 set(s => ({ pendingUploadTick: s.pendingUploadTick + 1 }));
 break;

 case 'transcode_progress': {
 const p = message.payload || message.data;
 if (p?.fileHash) {
 useFileTranscodeStore.getState().applyProgress(
 p.fileHash,
 p.variantResolution ?? null,
 { percent: p.percent ?? 0, etaSec: p.etaSec ?? null, stage: p.stage ?? null }
 );
 }
 break;
 }
 case 'transcode_complete': {
 const p = message.payload || message.data;
 if (p?.fileHash) {
 useFileTranscodeStore.getState().applyComplete(
 p.fileHash,
 p.variantResolution ?? null,
 { codec: p.codec, fps: p.fps, durationSec: p.durationSec }
 );
 }
 break;
 }
 case 'transcode_failed': {
 const p = message.payload || message.data;
 if (p?.fileHash) {
 useFileTranscodeStore.getState().applyFailed(
 p.fileHash,
 p.variantResolution ?? null,
 p.error ?? 'unknown error'
 );
 }
 break;
 }
 }
 
 // Dispatch to moFamilyStore if needed
 if (message.type === 'slot_added' || 
 message.type === 'slot_removed' || 
 message.type === 'slot_updated' ||
 message.type === 'slot_set_switched' ||
 message.type === 'slot_synchronized' ||
 message.type === 'family_invitation_sent' ||
 message.type === 'family_invitation_countered' ||
 message.type === 'family_relationship_created' ||
 message.type === 'family_relationship_deleted') {
 moFamilyStore.getState().handleWebSocketMessage(message);
 }
 }
}));

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
 const { isAuthenticated, isLoading } = useAppStore();
 
 if (isLoading) {
 return (
 <div className="flex items-center justify-center min-h-screen">
 <div className="text-center">
 <div className="animate-spin h-12 w-12 rounded-full border-4 border-gray-200 border-t-blue-600 mx-auto mb-4"></div>
 <p className="text-gray-600">로딩 중...</p>
 </div>
 </div>
 );
 }
 
 return isAuthenticated ? <>{children}</> : <Navigate to="/" replace />;
};

// Main App Component
const App: React.FC = () => {
 const { checkAuth } = useAppStore();
 
 useEffect(() => {
 checkAuth();
 // React 버전 확인용 로그 제거
 }, []);
 
 return (
 <Router>
 <NotificationToast />
 <Routes>
 <Route path="/" element={<LoginPage />} />
 <Route path="/login" element={<LoginPage />} />
 <Route path="/verify-invoice" element={<VerifyInvoicePage />} />
 <Route path="/verify-invoice/:id" element={<VerifyInvoicePage />} />
 <Route path="/dashboard" element={
 <ProtectedRoute>
 <Layout>
 <DashboardPage />
 </Layout>
 </ProtectedRoute>
 } />
 <Route path="/mofamily" element={
 <ProtectedRoute>
 <Layout>
 <MoFamilyPage />
 </Layout>
 </ProtectedRoute>
 } />
 <Route path="/mofamily/:moFamilyHash" element={
 <ProtectedRoute>
 <Layout>
 <MoFamilyPage />
 </Layout>
 </ProtectedRoute>
 } />
 <Route path="/mofamily-invitation" element={
 <ProtectedRoute>
 <Layout>
 <MoFamilyInvitationPage />
 </Layout>
 </ProtectedRoute>
 } />
 <Route path="/mofile" element={
 <ProtectedRoute>
 <Layout>
 <MoFilePage />
 </Layout>
 </ProtectedRoute>
 } />
 <Route path="/basic" element={
 <ProtectedRoute>
 <Layout>
 <BasicPage />
 </Layout>
 </ProtectedRoute>
 } />
 {/* 일반 사용자용 기기 설치 가이드 (로그인만 필요, admin 아님) */}
 <Route path="/device-guide" element={
 <ProtectedRoute>
 <Layout>
 <DeviceGuide />
 </Layout>
 </ProtectedRoute>
 } />
 {/* 인스타그램 플러그인(MoScraper) 가이드 */}
 <Route path="/plugin-guide" element={
 <ProtectedRoute>
 <Layout>
 <PluginGuide />
 </Layout>
 </ProtectedRoute>
 } />
 {/* 기존 /momail 은 통합 /inbox 로 redirect — 북마크/외부 링크 호환성. */}
 <Route path="/momail" element={<Navigate to="/inbox" replace />} />
 <Route path="/inbox" element={
 <ProtectedRoute>
 <Layout>
 <InboxV2Page />
 </Layout>
 </ProtectedRoute>
 } />
 <Route path="/inbox/:cid" element={
 <ProtectedRoute>
 <Layout>
 <ConversationViewPage />
 </Layout>
 </ProtectedRoute>
 } />
 <Route path="/profile" element={
 <ProtectedRoute>
 <Layout>
 <ProfilePage />
 </Layout>
 </ProtectedRoute>
 } />
 <Route path="/campaign" element={
 <ProtectedRoute>
 <Layout>
 <CampaignPage />
 </Layout>
 </ProtectedRoute>
 } />
 <Route path="/campaign/new" element={
 <ProtectedRoute>
 <Layout>
 <CampaignForm />
 </Layout>
 </ProtectedRoute>
 } />
 <Route path="/campaign/:campaignHash" element={
 <ProtectedRoute>
 <Layout>
 <CampaignDetail />
 </Layout>
 </ProtectedRoute>
 } />
 <Route path="/campaign/:campaignHash/edit" element={
 <ProtectedRoute>
 <Layout>
 <CampaignForm />
 </Layout>
 </ProtectedRoute>
 } />
 <Route path="/moclient" element={
 <ProtectedRoute>
 <Layout>
 <MoClientPage />
 </Layout>
 </ProtectedRoute>
 } />
 {/* <Route path="/mocontroller" element={
 <ProtectedRoute>
 <Layout>
 <MoControllerPage />
 </Layout>
 </ProtectedRoute>
 } /> */}
 {/* /mopeer SPA — 회원 페이지는 /admin/users 로 통합 */}
 <Route path="/mopeer" element={<Navigate to="/admin/users" replace />} />
 {/* 마켓은 비로그인 접근 가능 — Layout 안에서 래핑하지만 ProtectedRoute 없음.
     비로그인 사용자도 둘러볼 수 있고, 광고주 PII 는 백엔드가 마스킹함. */}
 <Route path="/market" element={<Layout><MarketLanding /></Layout>} />
 {/* /market/proposals 도 통합 /inbox 로 redirect. /market/proposals/:hash 는 그대로 — thread direct 진입용. */}
 <Route path="/market/proposals" element={<Navigate to="/inbox" replace />} />
 <Route path="/market/proposals/:hash" element={
 <ProtectedRoute><Layout><ProposalThread /></Layout></ProtectedRoute>
 } />
 <Route path="/market/transactions" element={
 <ProtectedRoute><Layout><Transactions /></Layout></ProtectedRoute>
 } />
 <Route path="/admin/market" element={
 <ProtectedRoute><Layout><MarketAdmin /></Layout></ProtectedRoute>
 } />
 <Route path="/admin/users" element={
 <ProtectedRoute><Layout><UsersAdmin /></Layout></ProtectedRoute>
 } />
 <Route path="/admin/device-setup" element={
 <ProtectedRoute><Layout><DeviceSetupGuide /></Layout></ProtectedRoute>
 } />
 <Route path="/admin/proposals" element={
 <ProtectedRoute><Layout><ProposalPages /></Layout></ProtectedRoute>
 } />
 <Route path="/admin/moboard" element={
 <ProtectedRoute><Layout><MoboardCollector /></Layout></ProtectedRoute>
 } />
 <Route path="/market/:listingHash" element={<Layout><ListingDetail /></Layout>} />
 <Route path="/policy/admin-access" element={<AdminAccessPolicy />} />
 {/* 행사 현황 캘린더 임베드 위젯 — 타 사이트 iframe 용(무인증·Layout 없음). /embed/:handle. */}
 <Route path="/embed/:handle" element={<EmbedCalendar />} />
 {/* 비로그인 익명 영상 업로드 공개 페이지 — mocombinator.com/@{handle}. 로그인/Layout 불필요.
     React Router 7 은 파라미터가 '/' 직후여야 인식(/@:handle 매칭 불가)하므로 단일 세그먼트로
     받고, 컴포넌트가 '@' 접두사를 검사해 처리(비-@ 경로는 not-found). 정적 라우트가 우선순위 높아
     /dashboard 등 기존 경로는 영향 없음. */}
 <Route path="/:handle" element={<PublicUploadPage />} />
 </Routes>
 </Router>
 );
};

// Extend window interface
declare global {
 interface Window {
 moFamilyStore?: any;
 }
}

export default App;